import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const extractPlaylistId = (url) => {
  if (!url) return null;
  const match = url.match(/playlist\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
};

const FloatingSpotifyPlayer = () => {
  const { t } = useTranslation();
  const [playlistUrl, setPlaylistUrl] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenSeen, setHasBeenSeen] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/settings`);
        setPlaylistUrl(response.data.spotify_playlist_url || '');
      } catch (error) {
        // silent fail
      }
    };
    fetchSettings();

    // Remember if user has already opened the player once (don't be annoying)
    const seen = localStorage.getItem('spotify_player_seen');
    if (seen) setHasBeenSeen(true);
  }, []);

  const playlistId = extractPlaylistId(playlistUrl);
  if (!playlistId) return null;

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      localStorage.setItem('spotify_player_seen', '1');
      setHasBeenSeen(true);
    }
  };

  return (
    <div
      className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 z-40"
      data-testid="floating-spotify-player"
    >
      <AnimatePresence mode="wait">
        {isOpen ? (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="w-[min(360px,calc(100vw-2rem))] rounded-2xl overflow-hidden bg-[#0B0B0C] border border-[#1DB954]/40 shadow-[0_20px_60px_-10px_rgba(29,185,84,0.4)]"
          >
            <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-[#1DB954]/20 to-transparent border-b border-white/10">
              <div className="flex items-center gap-2">
                <Music className="w-4 h-4 text-[#1DB954]" />
                <span className="text-xs font-display uppercase tracking-wider text-[#F5F0E8]">
                  {t('spotify.title')}
                </span>
              </div>
              <button
                onClick={toggleOpen}
                aria-label={t('spotify.close')}
                className="p-1 rounded hover:bg-white/10 text-[#B9B2A8] hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <iframe
              title="Invasion Latina Spotify Playlist (mini)"
              src={`https://open.spotify.com/embed/playlist/${playlistId}?utm_source=generator&theme=0`}
              width="100%"
              height="352"
              frameBorder="0"
              allowFullScreen
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
            />
          </motion.div>
        ) : (
          <motion.button
            key="collapsed"
            onClick={toggleOpen}
            aria-label={t('spotify.play_button')}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            className="relative group w-14 h-14 rounded-full bg-[#1DB954] text-black shadow-[0_8px_30px_-5px_rgba(29,185,84,0.6)] flex items-center justify-center overflow-hidden"
          >
            {/* Pulse halo (only before first open) */}
            {!hasBeenSeen && (
              <>
                <span className="absolute inset-0 rounded-full bg-[#1DB954] opacity-60 animate-ping" aria-hidden="true" />
                <span className="absolute inset-0 rounded-full bg-[#1DB954] opacity-40 animate-ping" style={{ animationDelay: '0.7s' }} aria-hidden="true" />
              </>
            )}
            <Music className="w-6 h-6 relative z-10" />
            {/* Tooltip */}
            <span className="absolute right-full mr-3 px-3 py-1.5 bg-black/90 text-white text-xs font-display uppercase tracking-wider rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              {t('spotify.play_button')}
            </span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FloatingSpotifyPlayer;
